import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { AlertTriangle, AlertCircle, Info, XCircle, Check, X, Bell, Loader2, ExternalLink } from 'lucide-react';
import { adminAlertsAPI, adminRefundsAPI } from '../../services/api';

const severityConfig = {
  critical: {
    icon: XCircle,
    bgColor: 'bg-red-50 dark:bg-red-900/20',
    borderColor: 'border-red-200 dark:border-red-800',
    iconColor: 'text-red-500',
    badge: 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300'
  },
  warning: {
    icon: AlertTriangle,
    bgColor: 'bg-yellow-50 dark:bg-yellow-900/20',
    borderColor: 'border-yellow-200 dark:border-yellow-800',
    iconColor: 'text-yellow-500',
    badge: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/50 dark:text-yellow-300'
  },
  error: {
    icon: AlertCircle,
    bgColor: 'bg-red-50 dark:bg-red-900/20',
    borderColor: 'border-red-200 dark:border-red-800',
    iconColor: 'text-red-500',
    badge: 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300'
  },
  info: {
    icon: Info,
    bgColor: 'bg-blue-50 dark:bg-blue-900/20',
    borderColor: 'border-blue-200 dark:border-blue-800',
    iconColor: 'text-blue-500',
    badge: 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300'
  }
};

export default function AdminAlertsWidget({ limit = 5 }) {
  const queryClient = useQueryClient();
  const [showAll, setShowAll] = useState(false);

  const { data: alertsData, isLoading } = useQuery({
    queryKey: ['adminAlerts', { limit: showAll ? 20 : limit, status: 'unread' }],
    queryFn: () => adminAlertsAPI.list({ limit: showAll ? 20 : limit, status: 'unread' }),
    refetchInterval: 60000, // Refresh every minute
  });

  const { data: summaryData } = useQuery({
    queryKey: ['adminAlertsSummary'],
    queryFn: () => adminAlertsAPI.getSummary(),
    refetchInterval: 60000,
  });

  const markAsReadMutation = useMutation({
    mutationFn: (id) => adminAlertsAPI.markAsRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminAlerts'] });
      queryClient.invalidateQueries({ queryKey: ['adminAlertsSummary'] });
    }
  });

  const dismissMutation = useMutation({
    mutationFn: (id) => adminAlertsAPI.dismiss(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminAlerts'] });
      queryClient.invalidateQueries({ queryKey: ['adminAlertsSummary'] });
    }
  });

  // "Retry ConnectWise update" / "Update ConnectWise" on a refund alert: the
  // same path the refund dialog takes. Marks the alert read when it worked.
  const [cwActionError, setCwActionError] = useState(null);
  const refundCwMutation = useMutation({
    mutationFn: async (alert) => {
      setCwActionError(null);
      const res = await adminRefundsAPI.updateConnectWise(alert.details.refundId);
      const cw = res?.data?.connectwise;
      if (cw?.status !== 'reflected') throw new Error(cw?.error || 'ConnectWise was not updated.');
      await adminAlertsAPI.markAsRead(alert.id);
      return res;
    },
    onError: (err) => setCwActionError(err.response?.data?.error || err.message),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['adminAlerts'] });
      queryClient.invalidateQueries({ queryKey: ['adminAlertsSummary'] });
    }
  });

  const markAllReadMutation = useMutation({
    mutationFn: () => adminAlertsAPI.markAllAsRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminAlerts'] });
      queryClient.invalidateQueries({ queryKey: ['adminAlertsSummary'] });
    }
  });

  const alerts = alertsData?.data?.alerts || [];
  const summary = summaryData?.data || {};
  const unreadCount = summary.unreadCount || 0;
  const criticalCount = summary.criticalCount || 0;

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const getEntityLink = (alert) => {
    if (alert.entity_type === 'recurring_payment') {
      return `/admin/client-payments?company=${alert.company_id}&tab=recurring`;
    }
    if (alert.entity_type === 'payment_history') {
      return `/admin/client-payments?company=${alert.company_id}&tab=history`;
    }
    if (alert.entity_type === 'autopay_run') {
      return `/admin/client-payments?company=${alert.company_id}&tab=autopay`;
    }
    if (alert.entity_type === 'payment_method') {
      return `/admin/client-payments?company=${alert.company_id}&tab=methods`;
    }
    return null;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-neutral-400" />
      </div>
    );
  }

  if (alerts.length === 0 && unreadCount === 0) {
    return (
      <div className="text-center py-8 text-neutral-500 dark:text-neutral-400">
        <Bell className="w-8 h-8 mx-auto mb-2 text-neutral-300 dark:text-neutral-600" />
        <p>No alerts</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header with counts */}
      {(unreadCount > 0 || criticalCount > 0) && (
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            {criticalCount > 0 && (
              <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300 rounded-full">
                {criticalCount} critical
              </span>
            )}
            {unreadCount > criticalCount && (
              <span className="px-2 py-1 text-xs font-medium bg-neutral-100 text-neutral-700 dark:bg-neutral-700 dark:text-neutral-300 rounded-full">
                {unreadCount} unread
              </span>
            )}
          </div>
          {unreadCount > 0 && (
            <button
              onClick={() => markAllReadMutation.mutate()}
              disabled={markAllReadMutation.isPending}
              className="text-xs text-primary-600 hover:text-primary-700 dark:text-primary-400"
            >
              Mark all read
            </button>
          )}
        </div>
      )}

      {/* Alert list */}
      <div className="space-y-2">
        {alerts.map((alert) => {
          const severity = severityConfig[alert.severity] || severityConfig.info;
          const SeverityIcon = severity.icon;
          const entityLink = getEntityLink(alert);

          return (
            <div
              key={alert.id}
              className={`relative p-3 rounded-lg border ${severity.bgColor} ${severity.borderColor} ${
                !alert.is_read ? 'ring-1 ring-primary-200 dark:ring-primary-800' : ''
              }`}
            >
              <div className="flex items-start gap-3">
                <div className={`flex-shrink-0 p-1.5 rounded ${severity.badge}`}>
                  <SeverityIcon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-medium text-neutral-900 dark:text-white truncate">
                      {alert.title}
                    </span>
                    {!alert.is_read && (
                      <span className="w-2 h-2 rounded-full bg-primary-500 flex-shrink-0" />
                    )}
                  </div>
                  <p className="text-xs text-neutral-600 dark:text-neutral-400 line-clamp-2 mb-1">
                    {alert.message}
                  </p>
                  <div className="flex items-center gap-3 text-xs text-neutral-500 dark:text-neutral-500">
                    <span>{formatTimeAgo(alert.created_at)}</span>
                    {alert.company_name && (
                      <span className="truncate">{alert.company_name}</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {alert.details?.refundId && ['retry', 'update'].includes(alert.details?.connectwiseAction) && (
                    <button
                      onClick={() => refundCwMutation.mutate(alert)}
                      disabled={refundCwMutation.isPending}
                      className="px-2 py-0.5 text-xs rounded border border-primary-300 text-primary-700 hover:bg-primary-50 dark:border-primary-700 dark:text-primary-300 disabled:opacity-50"
                    >
                      {alert.details.connectwiseAction === 'retry' ? 'Retry ConnectWise update' : 'Update ConnectWise'}
                    </button>
                  )}
                  {entityLink && (
                    <Link
                      to={entityLink}
                      className="p-1 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300"
                      title="View details"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </Link>
                  )}
                  {!alert.is_read && (
                    <button
                      onClick={() => markAsReadMutation.mutate(alert.id)}
                      disabled={markAsReadMutation.isPending}
                      className="p-1 text-neutral-400 hover:text-green-600 dark:hover:text-green-400"
                      title="Mark as read"
                    >
                      <Check className="w-4 h-4" />
                    </button>
                  )}
                  <button
                    onClick={() => dismissMutation.mutate(alert.id)}
                    disabled={dismissMutation.isPending}
                    className="p-1 text-neutral-400 hover:text-red-600 dark:hover:text-red-400"
                    title="Dismiss"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {cwActionError && (
        <p className="mt-2 text-xs text-red-600 dark:text-red-400">{cwActionError}</p>
      )}

      {/* Show more/less toggle */}
      {alertsData?.data?.total > limit && (
        <div className="mt-4 text-center">
          <button
            onClick={() => setShowAll(!showAll)}
            className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
          >
            {showAll ? 'Show less' : `View all ${alertsData.data.total} alerts`}
          </button>
        </div>
      )}
    </div>
  );
}
